import { useEffect, useMemo, useRef } from "react";
import { useAutoScrollPref } from "@/lib/globalAutoScroll";

function tokenize(str) {
  return str.split(/\s+/).filter(Boolean);
}

/**
 * Renders source text as coloured word chips based on the user's live typing.
 * Auto-scrolls the container so the current word stays in view.
 *
 * Word statuses:
 *  - pending  → not typed yet (neutral, always visible)
 *  - current  → the word user is typing right now (amber outline)
 *  - correct  → typed word matches (green — hidden when wordHighlight = false)
 *  - incorrect→ typed word differs (red underline — hidden when mistakeHighlight = false)
 *
 * Props:
 *  source            source string
 *  typed             typed string
 *  lang              "en" | "hi" (used for font sizing + `lang` attribute)
 *  wordHighlight     bool — toggle the green "correct" highlight (default true)
 *  mistakeHighlight  bool — toggle the red-underline "incorrect" mark (default true)
 */
export default function LiveHighlighter({
  source,
  typed,
  lang = "en",
  wordHighlight = true,
  mistakeHighlight = true,
  autoScroll = true,
}) {
  const currentRef = useRef(null);
  const [globalAutoScrollOn] = useAutoScrollPref();
  const analysis = useMemo(() => {
    const src = tokenize(source);
    const t = tokenize(typed);
    const midWord = typed.length > 0 && !/\s$/.test(typed);
    const compareCount = midWord ? Math.max(0, t.length - 1) : t.length;
    return src.map((word, i) => {
      if (i < compareCount) {
        return { word, status: t[i] === word ? "correct" : "incorrect", typed: t[i] };
      }
      if (i === compareCount && midWord) {
        const partial = t[t.length - 1] || "";
        const matchesSoFar = word.startsWith(partial);
        return { word, status: "current", partialOk: matchesSoFar, typed: partial };
      }
      if (i === compareCount && !midWord) {
        return { word, status: "current", partialOk: true };
      }
      return { word, status: "pending" };
    });
  }, [source, typed]);

  const currentIndex = useMemo(() => analysis.findIndex((w) => w.status === "current"), [analysis]);

  useEffect(() => {
    // Respect the global auto-scroll master switch AND the per-panel prop.
    if (!autoScroll || !globalAutoScrollOn) return;
    if (currentRef.current) {
      currentRef.current.scrollIntoView({ block: "nearest", behavior: "smooth", inline: "nearest" });
    }
  }, [currentIndex, autoScroll, globalAutoScrollOn]);

  // Both modes: baseline chip color has explicit light + dark classes so the
  // word text is never lost against `bg-secondary` panels in either theme.
  const cls = {
    pending:
      "text-slate-700 dark:text-slate-200 border-transparent",
    current:
      "bg-amber-100 text-amber-900 border-amber-500 underline underline-offset-4 decoration-2 " +
      "dark:bg-amber-500/20 dark:text-amber-100 dark:border-amber-400",
    correct: wordHighlight
      ? "bg-green-100 text-green-800 border-green-300 " +
        "dark:bg-green-500/20 dark:text-green-100 dark:border-green-500/40"
      : "text-slate-700 dark:text-slate-200 border-transparent",
    incorrect: mistakeHighlight
      ? "bg-red-100 text-red-800 border-red-300 underline decoration-red-600 decoration-wavy decoration-2 underline-offset-4 " +
        "dark:bg-red-500/20 dark:text-red-100 dark:border-red-500/40 dark:decoration-red-400"
      : "text-slate-700 dark:text-slate-200 border-transparent",
  };

  return (
    <div
      lang={lang}
      className={`whitespace-normal practice-source-text ${lang === "hi" ? "is-hindi" : "font-mono"}`}
      data-testid="live-highlighter"
      data-word-highlight={wordHighlight ? "on" : "off"}
      data-mistake-highlight={mistakeHighlight ? "on" : "off"}
    >
      {analysis.map((w, i) => (
        <span
          key={i}
          ref={i === currentIndex ? currentRef : null}
          data-status={w.status}
          className={`inline-block m-0.5 px-1.5 py-0.5 border transition-colors ${cls[w.status]} ${
            w.status === "current" && !w.partialOk ? "!border-red-500 !text-red-700 dark:!text-red-300" : ""
          }`}
        >
          {w.word}
        </span>
      ))}
    </div>
  );
}
